import { TaskTemplate } from './../../shared/types/Task';
import { defaultSystemTask, TaskStatus } from "../../shared/types/Task";

const getTaskAge = (task: any, taskTemplates: any, theme: any) => {
    const isDarkMode = theme.palette.mode === 'dark';
    // Filter valid statuses for age calculation (exclude pending, cancelled, complete)
    const validStatuses = Object.values(TaskStatus).filter(
      (s) => s !== TaskStatus.pending && s !== TaskStatus.cancelled && s !== TaskStatus.complete
    );
    let totalMs = 0;
    let validPeriod = false;
    let lastStart: Date | null = null;
    const enabled = !!task.enabled;
    const status = task.status;
    let color = "";
    // Track last assigned user and their assignment period
    let lastAssignee: string | null = null;
    let lastUserAssignedFrom: number | null = null;
    let lastUserAssignededTo: number | null = null;
    let lastStatus: any;
    // Store assignment periods for each user
    const userAssignmentPeriods: Array<{ userId: string, from: number | null, to: number | null }> = [];
    // Store processed assignment periods with readable info
    let updatedUserAssignmentPeriods: Array<{ userId: string, from: Date | null, to: Date | null, days: number | null, hours: number | null, mins: number | null }> = [];
    // Defensive: sort activities by updatedAt ascending
    const activities = (task.activities || []).slice().sort((a: any, b: any) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
    // Use startedAt as initial reference if available
    const startedAt = task.startedAt ? new Date(task.startedAt) : null;

    // Determine if task is completed or cancelled
    const isCompleted = status === TaskStatus.complete;
    const isCancelled = status === TaskStatus.cancelled;
    let showNA = false;

    // If no activities, fallback to startedAt and current status/enabled
    if (activities.length === 0 && startedAt && enabled && validStatuses.includes(status)) {
      // Simple case: task started and is enabled, no activity log
      lastStart = startedAt;
      if ((isCompleted || isCancelled) && task.completedAt) {
        totalMs = new Date(task.completedAt).getTime() - lastStart.getTime();
      } else {
        totalMs = new Date().getTime() - lastStart.getTime();
      }
      validPeriod = false;
    } else {
      // Find initial reference point from enabled/start/status activities
      const enabledAct = activities.filter((a: any) => a.property === 'enabled' && (a.newValue === true || a.newValue === 'true'));
      const startedAtAct = activities.filter((a: any) => a.property === 'startedAt');
      const statusTodoAct = activities.filter((a: any) => a.property === 'status' && a.newValue === 'todo');
      const firstEnabled = enabledAct.length > 0 ? new Date(enabledAct[0].updatedAt) : null;
      const firstStartedAt = startedAtAct.length > 0 ? new Date(startedAtAct[0].updatedAt) : null;
      const firstStatusTodo = statusTodoAct.length > 0 ? new Date(statusTodoAct[0].updatedAt) : null;
      if ((task.taskTemplateID && (!startedAt || !firstEnabled || !firstStatusTodo))) {
        // No reference point at all, show NA
        showNA = true;
      } else {
        // Find the latest of the three initial points
        const initialPoint = [firstEnabled, firstStartedAt, firstStatusTodo].filter(Boolean).sort((a, b) => (a as Date).getTime() - (b as Date).getTime());
        const initialDate = initialPoint.length > 0 ? initialPoint[initialPoint.length - 1] : startedAt;

        if (isCancelled && !initialDate) {
          showNA = true;
        }

        // Start valid period from initialDate if enabled
        if (initialDate && enabled) {
          lastStart = initialDate;
          validPeriod = true;
          lastUserAssignedFrom = new Date(initialDate).getTime();
          lastUserAssignededTo = null;
          
          
          // Remove activities before initial point
          while (activities.length > 0 && new Date(activities[0].updatedAt).getTime() <= initialDate.getTime()) {
            activities.shift();
          }
          lastStatus = startedAtAct.newValue || status;
          lastAssignee = activities.find((a: any) => a.property === 'assignedUserID')?.oldValue || task.assignedUserID || null;

          // Process activity log for status/enabled/user assignment changes
        for (const activity of activities) {
          const activityTime = new Date(activity.updatedAt).getTime();

          // Track user assignment changes
          if (activity.property === 'assignedUserID') {
            // If user changes, record previous assignment period
            if (lastAssignee  && lastUserAssignedFrom) {
              lastUserAssignededTo = activityTime;
              userAssignmentPeriods.push({ userId: lastAssignee ? lastAssignee : '', from: lastUserAssignedFrom ? lastUserAssignedFrom : null, to: lastUserAssignededTo ? lastUserAssignededTo : null });
              lastAssignee = activity.newValue;
              lastUserAssignedFrom = activityTime;
              lastUserAssignededTo = null;
            }
          }

          // Handle enabled/disabled transitions
          if (activity.property === 'enabled') {
            if ((activity.newValue === true || activity.newValue === 'true') && (lastStatus && validStatuses.includes(lastStatus))) {
              // Task enabled: start new valid period
              if (!validPeriod) {
                lastStart = new Date(activity.updatedAt);
                validPeriod = true;
                lastUserAssignedFrom = activityTime;
                lastUserAssignededTo = null;
              }
            } else {
              // Task disabled: end valid period
              if (validPeriod && lastStart) {
                totalMs += activityTime - lastStart.getTime();
                validPeriod = false;
                lastStart = null;
                lastUserAssignededTo = activityTime;
                if (lastAssignee && lastUserAssignedFrom) {
                  userAssignmentPeriods.push({ userId: lastAssignee, from: lastUserAssignedFrom ? lastUserAssignedFrom : null, to: lastUserAssignededTo ? lastUserAssignededTo : null });
                  lastUserAssignedFrom = null;
                  lastUserAssignededTo = null;
                }
                
              }
            }
          } else if (activity.property === 'status') {
            // Handle status transitions
            if (!validStatuses.includes(activity.oldValue) && validStatuses.includes(activity.newValue)) {
              // Status changes to valid: start new valid period
              if (!validPeriod) {
                lastStart = new Date(activity.updatedAt);
                validPeriod = true;
                lastUserAssignedFrom = activityTime;
                lastUserAssignededTo = null;
              }
            } else if (validStatuses.includes(activity.oldValue) && !validStatuses.includes(activity.newValue)) {
              // Status changes from valid to invalid: end valid period
              if (validPeriod && lastStart) {
                totalMs += activityTime - lastStart.getTime();
                validPeriod = false;
                lastStart = null;
                lastUserAssignededTo = activityTime;
                if (lastAssignee && lastUserAssignedFrom) {
                  userAssignmentPeriods.push({ userId: lastAssignee, from: lastUserAssignedFrom ? lastUserAssignedFrom : null, to: lastUserAssignededTo ? lastUserAssignededTo : null });
                  lastUserAssignedFrom = null;
                  lastUserAssignededTo = null;
                }
              }
            }
            lastStatus = activity.newValue;
          }
        }

        // Handle cancelled tasks that were enabled and never completed
        if (isCancelled && enabled && !task.completedAt && initialDate && lastUserAssignedFrom && (lastStatus && validStatuses.includes(lastStatus))) {
          // Age till cancellation point (last activity)
          const cancellationTime = new Date(task.updatedAt).getTime();
          if (validPeriod && lastStart) {
            totalMs += cancellationTime - lastStart.getTime();
            validPeriod = false;
            lastStart = null;
            lastUserAssignededTo = cancellationTime;
            lastStatus = TaskStatus.cancelled;
            userAssignmentPeriods.push({ userId: lastAssignee ? lastAssignee : task.assignee.id, from: lastUserAssignedFrom ? lastUserAssignedFrom : null, to: lastUserAssignededTo ? lastUserAssignededTo : null });
            lastUserAssignedFrom = null;
            lastUserAssignededTo = null;
          }
        }
        }

      }
    }

    // If still in a valid period till now, add duration
    if (validPeriod && lastStart) {
      totalMs += new Date().getTime() - lastStart.getTime();
    }

    // Convert ms to days and hours for display
    let activeDays: any = 'NA';
    let activeHours: any = '';
    if (!showNA && totalMs > 0) {
      const days = Math.floor(totalMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((totalMs / (1000 * 60 * 60)) % 24);
      activeDays = days;
      activeHours = hours;

      if (userAssignmentPeriods.length > 0) {
        // Helper: get display name for userId
        const getUserNameFromID = (userId: string) => {
          const userDetails = task.allAssignedUserInfos.find((u: any) => u.id === userId);
          const user = userDetails ? userDetails.displayName : (userId === defaultSystemTask.systemTrackingTask ? defaultSystemTask.systemTrackingTask : userId);
          return user;
        };

        // Add open-ended assignment period for current assignee if task is still active
        const validTaskStatus = Object.values(TaskStatus).filter(
          (s) => s !== TaskStatus.pending && s !== TaskStatus.cancelled && s !== TaskStatus.complete);
        if (task.status && validTaskStatus.includes(task.status)) {
          userAssignmentPeriods.push({ userId: task?.assignee?.id, from: lastUserAssignedFrom ? new Date(lastUserAssignedFrom).getTime() : null, to: new Date().getTime() });
        }

        // Map assignment periods to readable info for tooltip
        updatedUserAssignmentPeriods = userAssignmentPeriods.map(period => {
          if (period.from && period.to) {
            const periodMs = period.to - period.from;
            const periodDays = Math.floor(periodMs / (1000 * 60 * 60 * 24));
            const periodHours = Math.floor((periodMs / (1000 * 60 * 60)) % 24);
            const periodMins = Math.floor((periodMs / (1000 * 60)) % 60);
            return { userId: getUserNameFromID(period.userId), from: new Date(period.from), to: new Date(period.to), days: periodDays, hours: periodHours, mins: periodMins };
          }
          return { userId: getUserNameFromID(period.userId), from: null, to: null, days: null, hours: null, mins: null };
        });
      }
    }
    // Color logic for age display (same as before)
    if (taskTemplates && task.taskTemplateID) {
      for (const newTask of taskTemplates) {
        if (task.taskTemplateID === newTask.id) {
          const taskAge: any = newTask?.taskAge ? newTask?.taskAge : null;
          color = activeDays > parseInt(taskAge) ? theme.palette.deepRed.solidRed ?? '' : (activeDays > parseInt(taskAge) / 2 ? theme.palette.yellow.main ?? '' : "");
        }
      }
    }
    const ageTextColor = color === theme.palette.deepRed.solidRed ? theme.palette.white.main : isDarkMode ? theme.palette.white.main : theme.palette.black.main;
    // Return both days and hours for display, and user assignment details for tooltip
    return { activeDays: activeDays !== 'NA' ? `${activeDays}` : 'NA', color, ageTextColor, userAssigmentDetails: updatedUserAssignmentPeriods };
  };

  export default getTaskAge;